/**
 * Centralized React Query key factory + cache policies.
 *
 * Why a factory?
 *  - Prevents scattered/duplicated keys (e.g. `["notifications"]` vs
 *    `["notifs", userId]`) which bypass dedup and cause stale-cache bugs.
 *  - Makes invalidation surgical: `qk.notifications.list(userId)`.
 *
 * Policies (Fase 6C.3):
 *  - `staleTime` is intentionally conservative. For anything financial,
 *    sessions, or subscriptions we keep tighter values and prefer realtime
 *    invalidation over long caching.
 *  - Realtime listeners must call `queryClient.invalidateQueries({ queryKey })`
 *    — they MUST NOT mutate cached data manually (avoids race conditions).
 */

export const STALE = {
  /** Almost-static admin lists (logs, audit, immutable history). */
  long: 5 * 60_000,
  /** General reads that change occasionally. */
  medium: 60_000,
  /** Lists that the user actively interacts with (notifications, dashboards). */
  short: 30_000,
  /** Financial / sessions / subscription state — refetch eagerly. */
  realtime: 0,
} as const;

export const GC = {
  default: 5 * 60_000,
  long: 15 * 60_000,
} as const;

/** Top-level scope so each entity has a unique, predictable cache namespace. */
export const qk = {
  notifications: {
    all: ["notifications"] as const,
    list: (userId: string | undefined) =>
      ["notifications", "list", userId ?? "anon"] as const,
    unreadCount: (userId: string | undefined) =>
      ["notifications", "unread-count", userId ?? "anon"] as const,
  },
  systemLogs: {
    all: ["system-logs"] as const,
    list: (filters?: Record<string, unknown>) =>
      ["system-logs", "list", filters ?? {}] as const,
  },
  admin: {
    all: ["admin"] as const,
    therapists: () => ["admin", "therapists"] as const,
    patients: () => ["admin", "patients"] as const,
    users: () => ["admin", "users"] as const,
    payments: () => ["admin", "payments"] as const,
    financial: () => ["admin", "financial"] as const,
    metrics: () => ["admin", "metrics"] as const,
    recentSessions: () => ["admin", "recent-sessions"] as const,
    logs: (filters?: Record<string, unknown>) =>
      ["admin", "logs", filters ?? {}] as const,
  },
  therapist: {
    all: ["therapist"] as const,
    dashboardSessions: (therapistId: string | undefined) =>
      ["therapist", "dashboard-sessions", therapistId ?? "anon"] as const,
    services: (therapistId: string | undefined) =>
      ["therapist", "services", therapistId ?? "anon"] as const,
    patients: (therapistId: string | undefined) =>
      ["therapist", "patients", therapistId ?? "anon"] as const,
    dismissedPatients: (therapistId: string | undefined) =>
      ["therapist", "dismissed-patients", therapistId ?? "anon"] as const,
    events: (therapistId: string | undefined) =>
      ["therapist", "events", therapistId ?? "anon"] as const,
    availability: (therapistId: string | undefined) =>
      ["therapist", "availability", therapistId ?? "anon"] as const,
    breaks: (therapistId: string | undefined) =>
      ["therapist", "breaks", therapistId ?? "anon"] as const,
    vacations: (therapistId: string | undefined) =>
      ["therapist", "vacations", therapistId ?? "anon"] as const,
  },
  patient: {
    all: ["patient"] as const,
    events: (userId: string | undefined) =>
      ["patient", "events", userId ?? "anon"] as const,
  },
  transactions: {
    all: ["transactions"] as const,
    list: (userId: string | undefined, filters?: Record<string, unknown>) =>
      ["transactions", "list", userId ?? "anon", filters ?? {}] as const,
    recurring: (userId: string | undefined) =>
      ["transactions", "recurring", userId ?? "anon"] as const,
  },
  // Reserved namespaces for Fase 6C.3C — DO NOT USE YET.
  sessions: {
    all: ["sessions"] as const,
    therapist: (
      therapistId: string | undefined,
      range?: { from: string; to: string },
    ) =>
      ["sessions", "therapist", therapistId ?? "anon", range ?? {}] as const,
    patient: (
      patientId: string | undefined,
      range?: { from: string; to: string },
    ) =>
      ["sessions", "patient", patientId ?? "anon", range ?? {}] as const,
  },
  subscriptions: { all: ["subscriptions"] as const },
} as const;
